import { useReducer } from "react";
import ShelfContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import { IShelf, IShelfDetails } from "../../../models/wms/shelf/response";
import { ICreateShelf, IUpdateShelf } from "../../../models/wms/shelf/request";
import { IShelfQuery } from "../../../models/wms/shelf/query";

export interface IProps {
  children: React.ReactNode;
}

const ShelfContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.wmsLayout.getAllShelves(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.wmsLayout.getShelf(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IShelfDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createShelf = async (request: ICreateShelf) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });

        await EndPoints.wmsLayout.createShelf(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateShelf = async (id: number, request: IUpdateShelf) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.updateShelf(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteShelf = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });

        await EndPoints.wmsLayout.deleteShelf(id);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const reorderShelf = async (id: number, pickSequence: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.reorderShelf(id, pickSequence);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IShelfQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <ShelfContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createShelf,
          updateShelf,
          deleteShelf,
          reorderShelf,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </ShelfContext.Provider>
  );
};

export default ShelfContextProvider;
